import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    User, Lock, CreditCard, BarChart3, 
    ShieldCheck, Mail, Crown 
} from 'lucide-react';
import AvatarPicker from '../components/AvatarPicker'; // Don't forget this!

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
    const navigate = useNavigate();
    const { logout, refreshUser } = useAuth(); // <--- 1. Grab the refresher
    
    // Data State
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Form State
    const [username, setUsername] = useState('');
    const [avatarColor, setAvatarColor] = useState('blue');
    const [avatarStyle, setAvatarStyle] = useState('google'); // <--- 2. Add Style State
    const [savingProfile, setSavingProfile] = useState(false);
    
    // Password State
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('subhack_token');
            const { data } = await axios.get(`${API}/user/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProfile(data);
            setUsername(data.username || '');
            setAvatarColor(data.avatar_color || 'blue');
            setAvatarStyle(data.avatar_style || 'google'); // <--- 3. Load saved style
        } catch (error) {
            toast.error("Failed to load profile");
            navigate('/login');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateProfile = async () => {
        if (!username.trim()) {
            toast.error("Username cannot be empty");
            return;
        }
        
        setSavingProfile(true);
        try {
            const token = localStorage.getItem('subhack_token');
            await axios.put(`${API}/user/profile`, {
                username,
                avatar_color: avatarColor,
                avatar_style: avatarStyle // <--- 4. Send style to API
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // <--- 5. THE MAGIC FIX: Force the top bar to update immediately
            if (refreshUser) {
                await refreshUser();
            }

            toast.success("Profile updated successfully");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to update profile");
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword) {
            toast.error("Please fill in both password fields");
            return;
        }
        
        setSavingPassword(true);
        try {
            const token = localStorage.getItem('subhack_token');
            await axios.post(`${API}/user/password`, {
                current_password: currentPassword,
                new_password: newPassword
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Password changed successfully");
            setCurrentPassword('');
            setNewPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to change password");
        } finally {
            setSavingPassword(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-12">
                    <Skeleton className="h-12 w-48 mb-8" />
                    <div className="grid gap-6 md:grid-cols-2">
                        <Skeleton className="h-64 rounded-xl" />
                        <Skeleton className="h-64 rounded-xl" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-background py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                        <div>
                            <h1 className="font-brand text-3xl md:text-4xl uppercase">
                                Account <span className="text-primary">Settings</span>
                            </h1>
                            <p className="text-muted-foreground mt-1">Manage your public profile and preferences</p>
                        </div>
                        <Button variant="outline" onClick={logout} className="shrink-0">
                            Sign Out
                        </Button>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="card-base p-4 flex flex-col items-center justify-center text-center">
                            <BarChart3 className="w-6 h-6 text-primary mb-2" />
                            <div className="font-heading text-2xl">{profile.total_views.toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground uppercase">Total Views</div>
                        </div>
                        <div className="card-base p-4 flex flex-col items-center justify-center text-center">
                            <CreditCard className="w-6 h-6 text-primary mb-2" />
                            <div className="font-heading text-2xl">{profile.clip_count}</div>
                            <div className="text-xs text-muted-foreground uppercase">Clips Created</div>
                        </div>
                        <div className="card-base p-4 flex flex-col items-center justify-center text-center col-span-2 md:col-span-2">
                            {profile.subscription_status === 'pro' ? (
                                <div className="flex flex-col items-center">
                                    <Crown className="w-6 h-6 text-yellow-400 mb-2" />
                                    <div className="font-heading text-2xl text-yellow-400">PRO MEMBER</div>
                                    <div className="text-xs text-muted-foreground uppercase">Plan Active</div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <User className="w-6 h-6 text-muted-foreground mb-2" />
                                    <div className="font-heading text-2xl">FREE PLAN</div>
                                    <Button 
                                        variant="link" 
                                        className="h-auto p-0 text-xs text-primary" 
                                        onClick={() => navigate('/pricing')}
                                    >
                                        Upgrade to Pro &rarr;
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        
                        {/* LEFT COLUMN: PUBLIC PROFILE */}
                        <div className="md:col-span-2 space-y-6">
                            <div className="card-base p-6">
                                <div className="flex items-center gap-2 mb-6 border-b border-border pb-4">
                                    <User className="w-5 h-5 text-primary" />
                                    <h2 className="font-heading text-lg uppercase">Public Profile</h2>
                                </div>

                                <div className="space-y-6">
                                    {/* Email (Read Only) */}
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase text-muted-foreground">Email Address</Label>
                                        <div className="flex items-center gap-2 bg-muted/50 p-3 rounded border border-border">
                                            <Mail className="w-4 h-4 text-muted-foreground" />
                                            <span className="text-sm font-mono text-muted-foreground">{profile.email}</span>
                                            <Lock className="w-3 h-3 text-muted-foreground ml-auto" />
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">Your email serves as your unique ID and cannot be changed.</p>
                                    </div>

                                    {/* Username */}
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase">Display Handle</Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">@</span>
                                            <Input 
                                                value={username} 
                                                onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                                                className="pl-7 input-base font-mono"
                                                placeholder="username"
                                                maxLength={20}
                                            />
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">Publicly visible. Letters, numbers, and underscores only.</p>
                                    </div>

                                    {/* Avatar Selection - REPLACED WITH NEW COMPONENT */}
                                    <div className="space-y-3">
                                        <AvatarPicker 
                                            currentStyle={avatarStyle} 
                                            currentAvatarUrl={profile.avatar_url}
                                            username={username}
                                            onSelect={setAvatarStyle} 
                                        />
                                    </div>

                                    <Button 
                                        className="btn-primary" 
                                        onClick={handleUpdateProfile}
                                        disabled={savingProfile}
                                    >
                                        {savingProfile ? 'Saving...' : 'Save Profile'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: SECURITY */}
                        <div className="space-y-6">
                            <div className="card-base p-6">
                                <div className="flex items-center gap-2 mb-6 border-b border-border pb-4">
                                    <ShieldCheck className="w-5 h-5 text-primary" />
                                    <h2 className="font-heading text-lg uppercase">Security</h2>
                                </div>

                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase">Current Password</Label>
                                        <Input 
                                            type="password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            className="input-base"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase">New Password</Label>
                                        <Input 
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="input-base"
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <Button 
                                            variant="outline" 
                                            className="w-full"
                                            onClick={handleChangePassword}
                                            disabled={savingPassword}
                                        >
                                            {savingPassword ? 'Updating...' : 'Update Password'}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Pro Badge (If Free) */}
                            {profile.subscription_status !== 'pro' && (
                                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
                                    <Crown className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <h3 className="font-heading text-sm uppercase mb-1">Go Pro</h3>
                                    <p className="text-xs text-muted-foreground mb-3">Unlock custom fonts, permanent links, and more.</p>
                                    <Button size="sm" className="btn-primary w-full" onClick={() => navigate('/pricing')}>
                                        View Plans
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}